import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '@/services/api';
import { qk } from '@/lib/queryKeys';
import { useTenantId } from './useTenant';

/**
 * GET /api/tenants/<id>/analytics?days=N returns { summary, trend,
 * driver_leaderboard, route_matrix, forecast, flagged_orders,
 * unpriced_materials }. Everything in it is computed from real order rows;
 * two figures are explicitly modelled rather than measured and the backend
 * labels them as such — fuel/toll cost (a per-tenant €/km assumption) and
 * loaded/empty distance (great-circle, not routed). The components that
 * render those say so in the UI rather than passing an estimate off as a
 * measurement.
 *
 * Analytics is the most expensive query in the app (it scans the order
 * history), so it gets a longer staleTime than the operational views and is
 * deliberately NOT invalidated by routine live updates — a single new order
 * does not move a 30-day average enough to justify the rescan.
 */
export function useAnalytics(days = 30) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.analytics(tenantId, days),
    queryFn: () => api(`/api/tenants/${tenantId}/analytics?days=${days}`),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });
}

/**
 * V-67. The per-order rows behind the Analytics aggregates —
 * GET /api/tenants/<id>/analytics/orders.
 *
 * Paged and tested since the aggregate response stopped carrying every
 * order inline, and nothing in frontend/src called it. So the tab said
 * "net profit EUR X" and "margin per tonne EUR Y" and a carrier who asked
 * the obvious next question — WHICH loads — had no way to see one.
 *
 * Measured on a live server: one route, 7 delivered loads, an average
 * margin of 230.23 EUR a load and a spread of 166.40 EUR between the best
 * and the worst. The route looks healthy on its average; three loads sit
 * 183.36 EUR below it between them, and no screen could name them.
 *
 * `sort` reaches the SERVER rather than being applied to the page here.
 * The rows are sorted before the page is cut, so "worst first" means worst
 * in the fleet; sorting one page of 100 in the browser would answer the
 * question for that page while looking like an answer for all of it.
 */
export function useAnalyticsOrders({ days = 30, sort = 'delivered_at', limit = 25, offset = 0 } = {}) {
  const tenantId = useTenantId();
  const query = useQuery({
    queryKey: qk.analyticsOrders(tenantId, days, sort, limit, offset),
    queryFn: () => api(`/api/tenants/${tenantId}/analytics/orders${
      qs({ days, sort: sort === 'margin' ? 'margin' : undefined, limit, offset })}`),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
    placeholderData: (previous) => previous,
  });
  return {
    ...query,
    data: query.data?.orders ?? [],
    totalCount: query.data?.total_count ?? 0,
    // Echoed by the server. A table headed "worst first" over a
    // newest-first page is the one mistake this field prevents.
    sort: query.data?.sort ?? 'delivered_at',
  };
}

/**
 * V-70. POST /api/tenants/<id>/fuel/link-card — attach every fill on one
 * card to one truck, in one action.
 *
 * The route has existed and been tested since the fuel importer, and
 * nothing in frontend/src called it. Its own docstring says why it exists
 * rather than an edit per row: a statement identifies the truck by CARD
 * NUMBER, so a first import lands sixty rows with no vehicle, "and sixty
 * individual edits is how the linking never happens — which leaves that
 * fuel out of every per-truck figure while the fleet total quietly includes
 * it."
 *
 * Measured on a live server: 60 fills on one card, 26 880 EUR. Fleet total
 * 26 880 EUR, attached to a truck 0 EUR, and the truck absent from the
 * per-vehicle table entirely. One call moved all sixty.
 *
 * `linked` in the reply is the row count, and 0 is a real and important
 * answer — the card number does not match — which is why the caller renders
 * it rather than assuming the 200 meant something moved.
 */
export function useLinkFuelCard() {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api(`/api/tenants/${tenantId}/fuel/link-card`, { method: 'POST', body }),
    // Both surfaces, because linking moves spend from the unlinked bucket
    // into a truck's own column: the fills list the carrier is looking at,
    // and the report whose per-vehicle table was missing the money.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fuel-fills'] });
      queryClient.invalidateQueries({ queryKey: ['fuel-report'] });
    },
  });
}

export const ANALYTICS_PERIODS = [7, 30, 90];

/** Series colors, matched to the monolith's ANALYTICS_COLORS so a saved
 *  screenshot of last quarter's chart still reads the same way. */
export const ANALYTICS_COLORS = {
  revenue: '#22D3EE',
  cost: '#F59E0B',
  margin: '#10B981',
  tonnage: '#14B8A6',
  dwell: '#F59E0B',
  empty: '#8A90A0',
  anomaly: '#EF4444',
};

/**
 * Unplanned downtime — hours each truck spent off the road because of a
 * defect, and what that cost when a rate is configured.
 *
 * A separate request from the analytics bundle rather than another key on
 * it: this reads `vehicle_defects`, which the analytics query never touches,
 * and its period is a pair of calendar dates rather than the tab's rolling
 * ?days= window. Folding it in would make the most expensive query in the
 * app scan a second table for a panel the reader may not have scrolled to.
 *
 * The bounds are part of the query key: changing them asks a different
 * question, and one cache entry across two of them would show last month's
 * hours under this month's heading.
 */
export function useDowntime(since, until) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.downtime(tenantId, since, until),
    queryFn: () => api(`/api/tenants/${tenantId}/downtime${qs({ since, until })}`),
    enabled: Boolean(tenantId),
    staleTime: 120_000,
  });
}

/**
 * Which drivers and which trucks keep leaving the quarry->site corridor,
 * over a period.
 *
 * Its own request rather than a key on the analytics bundle, for the same
 * reason useDowntime() above is: it reads columns the analytics query never
 * touches (orders.route_deviation_*), and its period is a pair of calendar
 * dates rather than that tab's rolling ?days= window.
 *
 * The bounds are part of the query key — changing them asks a different
 * question, and one cache entry across two of them would put last quarter's
 * scores under this quarter's heading.
 */
export function useRouteDeviation(since, until) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.routeDeviation(tenantId, since, until),
    queryFn: () => api(`/api/tenants/${tenantId}/route-deviation${qs({ since, until })}`),
    enabled: Boolean(tenantId),
    staleTime: 120_000,
  });
}

/**
 * What this carrier's diesel actually costs, from real fills.
 *
 * Its own request rather than a key on the analytics bundle: it reads
 * fuel_transactions, which the analytics query never touches, and its period
 * is a pair of calendar dates rather than that tab's rolling ?days= window.
 */
export function useFuelReport(since, until) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.fuelReport(tenantId, since, until),
    queryFn: () => api(`/api/tenants/${tenantId}/fuel-report${qs({ since, until })}`),
    enabled: Boolean(tenantId),
    staleTime: 120_000,
  });
}

/**
 * Which trucks are due in the workshop, and how much of the schedule the
 * carrier has actually been keeping.
 *
 * Two requests rather than one bundle: the due list is a PRESENT-TENSE
 * answer with no period at all — a truck is overdue today or it is not — and
 * the on-time share is a property of a chosen window of history. Folding them
 * into one key would re-fetch the due list every time somebody moved the
 * period, and would invalidate the share every time a service was recorded.
 */
/**
 * POST /api/tenants/<id>/fuel — one fill onto the record.
 *
 * The route has existed, validated and tested, since core/fuel.py shipped,
 * and nothing in the console called it: the only way to put diesel into this
 * product was curl. That matters because tenants.fuel_cost_per_km's 0.35
 * EUR/km guess — which AUDIT.md calls the most expensive assumption in the
 * product — is replaced only by a measurement made from these rows.
 *
 * Invalidates the same three keys useLiveSync already routes a `fuel` event
 * to, and the third is the non-obvious one: a fill carries an odometer
 * reading, and core/servicing.py resolves a truck's current odometer from
 * exactly those fills, so a fill entered here moves the maintenance
 * deadlines too.
 */
export function useCreateFuel() {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api(`/api/tenants/${tenantId}/fuel`, { method: 'POST', body }),
    // V-290. useUpdateFuel()/useDeleteFuel() below both invalidate
    // 'fuel-fills' too — the only one of the three fuel_transactions
    // mutations that didn't. FuelPanel always renders "+ Add fill"
    // (useCreateFuel) beside "Patikrinti" (FuelFillsModal, useFuelFills)
    // for the same truck; without this, a fill entered right after
    // checking a truck's list stayed invisible on that list for the
    // remaining 15s staleTime, looking like the save had failed.
    onSuccess: () => ['fuel-report', 'fuel-fills', 'vehicles', 'service-due'].forEach(
      (key) => queryClient.invalidateQueries({ queryKey: [key] })),
  });
}

/**
 * GET /api/tenants/<id>/fuel?vehicle_id=… — one truck's own fills.
 *
 * Exists because a finding has to be actionable. The consumption check names
 * a truck and a fortnight and says its odometer reading does not match — and
 * until this, there was no screen in the console where that reading could be
 * corrected. PUT and DELETE on a fill had been validated and tested since the
 * fuel engine shipped and NOTHING called them, which is exactly the dead end
 * AUDIT.md V-32 found in this same engine when the POST route had no screen.
 *
 * Scoped server-side rather than by filtering a page in the browser: a
 * carrier with more fills than one page would silently be shown the wrong
 * rows, with an odometer field open in front of him.
 */
/** Pass this instead of a vehicle id to list the fills belonging to NO
 *  vehicle.
 *
 *  V-58: blueprints/fuel.py has answered `?unlinked=1` since V-41, and its
 *  comment says why — "the one filter a carrier needs on this screen, and
 *  it is the state that keeps money out of every per-vehicle figure".
 *  Nothing in the console ever passed it, so the panel named the money
 *  ("Jokiai mašinai nepriskirtų pildymų: 2 (€852,00)") in a paragraph with
 *  no way to reach it. A sentinel rather than a second hook, because the
 *  modal, the query key and the invalidations are all the same otherwise —
 *  and because a truthy value keeps the `enabled` guard doing its job. */
export const FUEL_FILLS_UNLINKED = '__unlinked__';

export function useFuelFills(vehicleId) {
  const tenantId = useTenantId();
  const unlinked = vehicleId === FUEL_FILLS_UNLINKED;
  return useQuery({
    queryKey: qk.fuelFills(tenantId, vehicleId),
    queryFn: () => api(`/api/tenants/${tenantId}/fuel${
      qs(unlinked ? { unlinked: 1 } : { vehicle_id: vehicleId })}`),
    enabled: Boolean(tenantId && vehicleId),
  });
}

/**
 * PUT /api/fuel/<id> — correct a fill.
 *
 * Invalidates the fill list as well as the three keys a create invalidates:
 * the modal that opened on a flagged stretch is showing those same rows, and
 * a corrected odometer that left the list stale would look like an edit that
 * did not save.
 */
export function useUpdateFuel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fuelId, ...body }) => api(`/api/fuel/${fuelId}`, { method: 'PUT', body }),
    onSuccess: () => ['fuel-report', 'fuel-fills', 'vehicles', 'service-due'].forEach(
      (key) => queryClient.invalidateQueries({ queryKey: [key] })),
  });
}

/**
 * DELETE /api/fuel/<id> — a HARD delete, per that route's own docstring: the
 * reason a fill is deleted is that it should never have been there (a
 * duplicated statement line, a fill typed against the wrong carrier), and a
 * soft-deleted one would leave every future query to remember to filter it
 * out — one that forgets double-counts the diesel.
 */
export function useDeleteFuel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fuelId) => api(`/api/fuel/${fuelId}`, { method: 'DELETE' }),
    onSuccess: () => ['fuel-report', 'fuel-fills', 'vehicles', 'service-due'].forEach(
      (key) => queryClient.invalidateQueries({ queryKey: [key] })),
  });
}

export function useServiceDue() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.serviceDue(tenantId),
    queryFn: () => api(`/api/tenants/${tenantId}/service/due`),
    enabled: Boolean(tenantId),
    staleTime: 120_000,
  });
}

export function useServiceOnTime(since, until) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.serviceOnTime(tenantId, since, until),
    queryFn: () => api(`/api/tenants/${tenantId}/service/on-time${qs({ since, until })}`),
    enabled: Boolean(tenantId),
    staleTime: 120_000,
  });
}


/**
 * The month's reconciliation: what was hauled, what was billed, the gap.
 *
 * Keyed on the month, so moving the picker fetches that month rather than
 * re-deriving one already on screen — and so a month a carrier has closed
 * stays cached while he works through the next one.
 */
/**
 * How dense each driver's working day was over a window.
 *
 * Its own request rather than a key on the analytics bundle, for the same
 * reason useDowntime() is: it reads driver_location_pings, which the
 * analytics query never touches, and one fleet pinging every minute is a
 * table that has no business being joined into the tab's main answer.
 */
export function useDriverDays(since, until) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.driverDays(tenantId, since, until),
    queryFn: () => api(`/api/tenants/${tenantId}/driver-days${qs({ since, until })}`),
    enabled: Boolean(tenantId),
    staleTime: 120_000,
  });
}

/**
 * Transporto kainodaros modeliai: vairuotojų atlygio (dienos alga +
 * komisinis) santrauka. Same optional since/until, same server-side
 * 30-day-rolling default when both are omitted, as useDowntime()/
 * useDriverDays() right above.
 */
export function useDriverPayroll(since, until) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.driverPayroll(tenantId, since, until),
    queryFn: () => api(`/api/tenants/${tenantId}/driver-payroll${qs({ since, until })}`),
    enabled: Boolean(tenantId),
    staleTime: 120_000,
  });
}

export function useMonthClose(month) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.monthClose(tenantId, month),
    queryFn: () => api(`/api/tenants/${tenantId}/month-close${qs({ month })}`),
    enabled: Boolean(tenantId),
    staleTime: 120_000,
  });
}

/**
 * GET /api/tenants/<id>/standing-time — where the fleet's standing hours
 * went, and what they cost at the carrier's own truck-hour figure.
 *
 * A calendar month, not the tab's rolling ?days= window, and deliberately
 * the SAME month parameter the Prastovų Aktas takes: a carrier reading one
 * month's act and one month's standing time has to be reading one period,
 * or the two documents cannot be laid side by side.
 *
 * The month is part of the key for the reason useDowntime()'s dates are:
 * changing it asks a different question, and one cache entry serving both
 * would show August's hours under a September heading.
 */
export function useStandingTime(month) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.standingTime(tenantId, month),
    queryFn: () => api(`/api/tenants/${tenantId}/standing-time${qs({ month })}`),
    enabled: Boolean(tenantId),
    staleTime: 120_000,
  });
}

/**
 * GET /api/tenants/<id>/quote — what a load would keep at a given rate,
 * while the rate is still on the table.
 *
 * Everything this product knows about the money on a load, it has always
 * known one delivery too late: the route matrix reports a thin run after it
 * has been quoted that way forty times. This is the same arithmetic, asked
 * before the decision — see core/quote.py for what the figure is allowed to
 * claim and, in particular, for the one thing it cannot know (the empty leg
 * back, which makes every quote optimistic by the cost of the run home).
 *
 * `enabled` is the whole shape of this hook. A quote needs a route and a
 * material; without them the server would 400 and a form half-filled is the
 * ordinary state of a form. So it simply does not ask until it can.
 *
 * No debounce, deliberately: the query key carries every input, so TanStack
 * dedupes and caches per combination, and a dispatcher typing a rate flips
 * between answers already in the cache rather than re-firing. `tons` and
 * `pricePerTon` arrive as strings from the inputs and are normalised here so
 * "24" and "24.0" are one cache entry rather than two.
 */
export function useQuote({ quarryId, siteId, cargoType, tons, pricePerTon } = {}) {
  const tenantId = useTenantId();
  const numericTons = Number.parseFloat(tons);
  const numericPrice = Number.parseFloat(pricePerTon);
  const askableTons = Number.isFinite(numericTons) && numericTons > 0 ? numericTons : undefined;
  const askablePrice = Number.isFinite(numericPrice) && numericPrice > 0 ? numericPrice : undefined;
  const cargo = (cargoType ?? '').trim();

  return useQuery({
    queryKey: qk.quote(tenantId, quarryId, siteId, cargo, askableTons, askablePrice),
    queryFn: () => api(`/api/tenants/${tenantId}/quote${qs({
      quarry_id: quarryId, site_id: siteId, cargo_type: cargo,
      tons: askableTons, price_per_ton: askablePrice,
    })}`),
    enabled: Boolean(tenantId && quarryId && siteId && cargo && askableTons),
    staleTime: 60_000,
  });
}

/** The four calendar periods the manager summary understands, in the order
 *  the picker shows them: the two closed ones first, because a closed period
 *  is the one a review is actually about. */
export const MANAGER_PERIODS = ['last_week', 'last_month', 'week', 'month'];

/**
 * GET /api/tenants/<id>/manager-summary?period= — one calendar period of the
 * business on one page.
 *
 * A CALENDAR period, not the tab's rolling ?days= window, and that is the
 * whole reason it is a separate query rather than a slice of useAnalytics().
 * A manager comparing this month against last month needs the two to be the
 * same kind of thing, and two rolling 30-day windows a month apart overlap.
 *
 * Every figure in the answer is the analytics engine's own — the server
 * computes nothing new for this route (see _compute_manager_summary()) — so
 * a number here can never disagree with the same number further down the
 * tab.
 */
export function useManagerSummary(period = 'month') {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.managerSummary(tenantId, period),
    queryFn: () => api(`/api/tenants/${tenantId}/manager-summary${qs({ period })}`),
    enabled: Boolean(tenantId),
    staleTime: 120_000,
  });
}

/**
 * GET /api/tenants/<id>/route-plan-actual?days= — what each leg was planned
 * to cost against what it actually did.
 *
 * Its own query, and its own SHORTER default window, because this is the
 * only report in the product whose cost is proportional to GPS fixes rather
 * than to orders: a fix every 15 seconds is ~240 rows for a one-hour leg.
 * The server caps it at 90 days (the ping retention window, past which the
 * trail this is built on has been purged) and at 500 legs, and says when it
 * truncated.
 *
 * A long staleTime for the same reason useAnalytics() has one: this is a
 * report somebody opens, not a dashboard that has to keep up with a single
 * new delivery.
 */
/**
 * POST /api/tenants/<id>/route-cache/warm — switch this carrier onto real
 * road distances.
 *
 * The decision stays a human's, which is the whole point: tenants
 * .route_cache_enabled defaults to 0 so nobody watches their margin drop
 * 30-40 % between two page loads because the basis of every EUR/km silently
 * changed from straight-line to road distance. Until now that deliberate act
 * existed only as a CLI script run over SSH, so a carrier who never had an
 * installer visit could never make it. This is the same decision, made from
 * the console.
 *
 * A 200 with `enabled: false` is an ordinary answer, not a failure — "you
 * have no quarries or sites yet" and "OSRM did not answer" are both things
 * the panel renders as a sentence. Only a thrown error is an error.
 *
 * Invalidates the plan-vs-actual query on success: every leg in it was
 * refused for want of exactly this, so the report is a different report the
 * moment this lands.
 */
export function useWarmRouteCache() {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api(`/api/tenants/${tenantId}/route-cache/warm`, { method: 'POST' }),
    onSuccess: (result) => {
      if (!result?.enabled) return;
      queryClient.invalidateQueries({ queryKey: ['route-plan-actual', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['analytics', tenantId] });
    },
  });
}

export function usePlanVsActual(days = 7) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.planVsActual(tenantId, days),
    queryFn: () => api(`/api/tenants/${tenantId}/route-plan-actual${qs({ days })}`),
    enabled: Boolean(tenantId),
    staleTime: 120_000,
  });
}

/**
 * GET /api/tenants/<id>/client-satisfaction — the trend behind the B2B
 * portal's five stars.
 *
 * Its own window in months rather than the tab's rolling ?days= selector,
 * and twelve of them by default. A satisfaction line read over thirty days
 * is noise: this business triples its volume in summer, so "worse than last
 * month" says nothing until it can be compared with the same month last
 * year.
 *
 * staleTime is generous because the underlying rows move at the speed of
 * somebody tapping a star on a site, not at the speed of a dispatch board.
 */
export function useClientSatisfaction(months, clientId) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.clientSatisfaction(tenantId, months, clientId),
    queryFn: () =>
      api(`/api/tenants/${tenantId}/client-satisfaction${qs({ months, client_id: clientId })}`),
    enabled: Boolean(tenantId),
    staleTime: 300_000,
  });
}
